import React, { useState, useEffect } from 'react';
import { Package, Plus, Minus, AlertTriangle, Edit2, Trash2, History, Coffee, Wine, Droplet, X, Check, Loader2 } from 'lucide-react';
import api from '../../api/axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const CATEGORIES = {
  bebida: { label: 'Bebida', icon: Wine, color: 'text-blue-400' },
  cafe: { label: 'Café', icon: Coffee, color: 'text-amber-400' },
  otro: { label: 'Otro', icon: Package, color: 'text-gray-400' }
};

// Modal para agregar/editar item
const ItemModal = ({ isOpen, onClose, item, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'bebida',
    quantity: 0,
    min_stock: 5,
    unit: 'unidades',
    price: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        category: item.category || 'bebida',
        quantity: item.quantity || 0,
        min_stock: item.min_stock || 5,
        unit: item.unit || 'unidades',
        price: item.price || '',
        notes: item.notes || ''
      });
    } else {
      setFormData({
        name: '',
        category: 'bebida',
        quantity: 0,
        min_stock: 5,
        unit: 'unidades',
        price: '',
        notes: ''
      });
    }
  }, [item, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null
      };
      
      if (item) {
        await api.put(`/admin/inventory/${item.id}`, data);
        toast.success('Item actualizado');
      } else {
        await api.post('/admin/inventory', data);
        toast.success('Item creado');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <div 
        className="bg-background border border-white/10 rounded-xl w-full max-w-md max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-background">
          <h3 className="text-lg sm:text-xl font-bold">{item ? 'Editar Item' : 'Nuevo Item'}</h3>
          <button onClick={onClose} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Nombre *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-h-[48px]"
              required
              disabled={!!item}
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Categoría</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-h-[48px]"
              >
                <option value="bebida">Bebida</option>
                <option value="cafe">Café</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Unidad</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-h-[48px]"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Stock Mínimo</label>
              <input
                type="number"
                value={formData.min_stock}
                onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-h-[48px]"
                min="0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Precio (ARS)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-h-[48px]"
                min="0"
                placeholder="Opcional"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              rows="2"
              placeholder="Notas adicionales..."
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1 min-h-[48px] rounded-lg order-2 sm:order-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-primary min-h-[48px] rounded-lg order-1 sm:order-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : item ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para ajustar stock
const AdjustStockModal = ({ isOpen, onClose, item, onSave }) => {
  const [adjustType, setAdjustType] = useState('entrada');
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !item) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/inventory/${item.id}/adjust`, {
        type: adjustType,
        quantity: parseInt(quantity),
        reason
      });
      toast.success('Stock actualizado');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ajustar stock');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-background border border-white/10 rounded-xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-bold">Ajustar Stock</h3>
          <p className="text-sm text-muted-foreground mt-1">{item.name}</p>
          <p className="text-lg font-bold text-primary mt-2">Stock actual: {item.quantity} {item.unit}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tipo de movimiento</label>
            <div className="grid grid-cols-3 gap-2">
              {['entrada', 'salida', 'ajuste'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAdjustType(type)}
                  className={`p-2 rounded-lg border-2 transition-all capitalize ${
                    adjustType === type 
                      ? type === 'entrada' ? 'border-green-500 bg-green-500/20'
                        : type === 'salida' ? 'border-red-500 bg-red-500/20'
                        : 'border-blue-500 bg-blue-500/20'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  {type === 'entrada' && <Plus className="h-4 w-4 mx-auto mb-1 text-green-400" />}
                  {type === 'salida' && <Minus className="h-4 w-4 mx-auto mb-1 text-red-400" />}
                  {type === 'ajuste' && <Edit2 className="h-4 w-4 mx-auto mb-1 text-blue-400" />}
                  <span className="text-xs">{type}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">
              {adjustType === 'ajuste' ? 'Nuevo stock' : 'Cantidad'}
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-center text-xl"
              min="1"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Motivo (opcional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ej: Reposición semanal"
            />
          </div>
          
          {adjustType !== 'ajuste' && (
            <div className="p-3 bg-white/5 rounded-lg text-sm">
              <p className="text-muted-foreground">
                Nuevo stock: <span className="font-bold text-primary">
                  {adjustType === 'entrada' 
                    ? item.quantity + parseInt(quantity || 0)
                    : Math.max(0, item.quantity - parseInt(quantity || 0))
                  } {item.unit}
                </span>
              </p>
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-primary">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Componente principal
export const InventoryManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await api.get('/admin/inventory');
      setItems(response.data.items);
      setLowStockCount(response.data.low_stock_count);
    } catch (error) {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const response = await api.post('/admin/inventory/initialize');
      toast.success(response.data.message);
      fetchInventory();
    } catch (error) {
      toast.error('Error al inicializar inventario');
    } finally {
      setInitializing(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.name}" del inventario?`)) return;
    
    try {
      await api.delete(`/admin/inventory/${item.id}`);
      toast.success('Item eliminado');
      fetchInventory();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  const openAdjustModal = (item) => {
    setSelectedItem(item);
    setShowAdjustModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase">Control de Stock</h2>
          <p className="text-muted-foreground">Gestión de inventario del local</p>
        </div>
        
        <div className="flex gap-2">
          {items.length === 0 && (
            <Button 
              onClick={handleInitialize} 
              disabled={initializing}
              variant="outline"
            >
              {initializing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
              Cargar Items Default
            </Button>
          )}
          <Button onClick={() => { setSelectedItem(null); setShowItemModal(true); }} className="bg-primary">
            <Plus className="h-4 w-4 mr-2" />
            Agregar Item
          </Button>
        </div>
      </div>

      {/* Alerta de stock bajo */}
      {lowStockCount > 0 && (
        <Card className="border-yellow-500/30 bg-yellow-500/10">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <p className="text-yellow-300">
              <strong>{lowStockCount} item{lowStockCount > 1 ? 's' : ''}</strong> con stock bajo. Revisá el inventario.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Lista de items */}
      {items.length === 0 ? (
        <Card className="glass-effect border-white/10">
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No hay items en el inventario</p>
            <Button onClick={handleInitialize} disabled={initializing}>
              {initializing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Cargar Items por Defecto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => {
            const category = CATEGORIES[item.category] || CATEGORIES.otro;
            const CategoryIcon = category.icon;
            
            return (
              <Card 
                key={item.id} 
                className={`glass-effect transition-all ${
                  item.is_low_stock 
                    ? 'border-yellow-500/50 bg-yellow-500/5' 
                    : 'border-white/10 hover:border-white/20'
                }`}
                data-testid={`inventory-item-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-white/10 ${category.color}`}>
                        <CategoryIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{item.name}</h3>
                        <p className="text-xs text-muted-foreground">{category.label}</p>
                      </div>
                    </div>
                    
                    {item.is_low_stock && (
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Bajo
                      </span>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className={`text-3xl font-bold ${item.is_low_stock ? 'text-yellow-400' : 'text-primary'}`}>
                        {item.quantity}
                      </span>
                      <span className="text-muted-foreground">{item.unit}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Mínimo: {item.min_stock} {item.unit}
                    </p>
                    {item.price && (
                      <p className="text-sm text-primary mt-1">
                        ${item.price.toLocaleString('es-AR')}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => openAdjustModal(item)}
                      className="flex-1 bg-primary/20 hover:bg-primary/30 text-primary"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ajustar
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => openEditModal(item)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDelete(item)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modales */}
      <ItemModal 
        isOpen={showItemModal}
        onClose={() => { setShowItemModal(false); setSelectedItem(null); }}
        item={selectedItem}
        onSave={fetchInventory}
      />
      
      <AdjustStockModal
        isOpen={showAdjustModal}
        onClose={() => { setShowAdjustModal(false); setSelectedItem(null); }}
        item={selectedItem}
        onSave={fetchInventory}
      />
    </div>
  );
};

export default InventoryManager;
